import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { toast } from "sonner";
import { Copy, Gift as GiftIcon, Loader2 } from "lucide-react";

const GIFT_TYPES = [
  { id: "first_consult", name: "First Consult", price: "$225", note: "The whole story, start to wherever it lands." },
  { id: "ongoing_single", name: "Ongoing Session", price: "$200", note: "One bloke, one hour, no small print." },
  { id: "couples_session", name: "Couples Session", price: "$250", note: "For the pair who've stopped hearing each other." },
];

export function Gift() {
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState("first_consult");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const buy = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/gifts/checkout", {
        session_type: selected,
        origin_url: window.location.origin,
        buyer_email: buyerEmail,
        recipient_note: note || null,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Something jammed. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-hearth-bg min-h-screen" data-testid="gift-page">
      <Seo
        title="Gift a Session | No BS Counselling"
        description="Buy a session for the bloke who'd never book one himself. You pay, you get a code, he turns up."
        path="/gift"
      />
      <Nav />
      <main className="pt-40 pb-24 max-w-3xl mx-auto px-6">
        <p className="eyebrow mb-4">For the One Who'd Never Book Himself</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-hearth-ink leading-tight">Buy the session. Hand over the code.</h1>
        <p className="mt-6 font-essay text-xl text-hearth-ink/90 leading-loose max-w-xl">
          Everyone knows a bloke who'd never book this himself. So you book it. You pay, you get a code,
          you put it in a card or say it over a beer. He turns up — or he doesn't. Either way, the door's open.
        </p>
        <div className="mt-12 space-y-4">
          {GIFT_TYPES.map((g) => (
            <button key={g.id} type="button" data-testid={`gift-type-${g.id}`} onClick={() => setSelected(g.id)}
              className={`w-full text-left flex items-center justify-between border p-6 transition-colors ${selected === g.id ? "border-hearth-ember bg-hearth-surface" : "border-hearth-line hover:border-hearth-ochre"}`}>
              <div>
                <p className="font-display font-bold text-xl text-hearth-ink">{g.name}</p>
                <p className="text-hearth-muted text-sm mt-1">{g.note}</p>
              </div>
              <span className="font-display font-bold text-2xl text-hearth-ochre">{g.price}</span>
            </button>
          ))}
        </div>
        <div className="mt-8 space-y-4">
          <input data-testid="gift-buyer-email" type="email" required value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)}
            placeholder="Your email (so the code never gets lost)"
            className="w-full bg-hearth-surface border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre" />
          <textarea data-testid="gift-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
            placeholder="A note that travels with the gift — optional. 'Just go. Once.' works."
            className="w-full bg-hearth-surface border border-hearth-line px-5 py-4 text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre font-essay text-lg" />
        </div>
        <button data-testid="gift-buy-button" disabled={!buyerEmail || submitting} onClick={buy}
          className="mt-8 inline-flex items-center gap-3 bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg disabled:opacity-30 disabled:cursor-not-allowed text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <GiftIcon className="w-4 h-4" />}
          Pay & Get the Code
        </button>
        <p className="mt-6 text-hearth-muted text-xs leading-relaxed">
          Paid through Stripe — card details never touch this site. Each code covers one session of the kind you pick,
          and works once. Family sessions aren't giftable — they're priced by conversation.
        </p>
      </main>
      <Footer config={config} />
    </div>
  );
}

export function GiftSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [gift, setGift] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!sessionId) { setChecking(false); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/gifts/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setGift(res.data);
          setChecking(false);
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setChecking(false);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="bg-hearth-bg min-h-screen" data-testid="gift-success-page">
      <Nav />
      <main className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-left">
        {checking && (
          <p className="text-hearth-muted flex items-center gap-3 font-essay italic text-xl" data-testid="gift-checking">
            <Loader2 className="w-5 h-5 animate-spin" /> Confirming with the bank…
          </p>
        )}
        {!checking && gift && (
          <div data-testid="gift-revealed">
            <h1 className="font-display font-bold text-4xl text-hearth-ink">Paid. Here's the code.</h1>
            <p className="mt-4 font-essay text-xl text-hearth-ink/90">One {gift.session_label?.toLowerCase()}, paid in full. Hand it over however you like.</p>
            <div className="mt-10 border border-hearth-ochre bg-hearth-surface p-10 deco-frame">
              <p className="eyebrow mb-4">The Code</p>
              <p className="font-display font-black text-4xl sm:text-5xl text-hearth-ochre tracking-wider" data-testid="gift-code">{gift.code}</p>
              {gift.recipient_note && <p className="mt-6 font-essay italic text-xl text-hearth-ink/80">"{gift.recipient_note}"</p>}
            </div>
            <button data-testid="gift-copy-button" onClick={() => { navigator.clipboard.writeText(gift.code); toast.success("Copied. Go change a Saturday."); }}
              className="mt-6 inline-flex items-center gap-3 border border-hearth-line hover:border-hearth-ochre text-hearth-muted hover:text-hearth-ink text-xs font-semibold uppercase tracking-widest px-6 py-3 transition-colors">
              <Copy className="w-4 h-4" /> Copy the Code
            </button>
            <p className="mt-8 text-hearth-muted text-sm">
              He redeems it at the booking page — there's a spot for gift codes. The code works once.
            </p>
          </div>
        )}
        {!checking && !gift && (
          <div data-testid="gift-pending-note">
            <h1 className="font-display font-bold text-4xl text-hearth-ink">One moment.</h1>
            <p className="mt-4 font-essay text-xl text-hearth-ink/90">The payment's taking the long way round. If money left your card, the code is safe — check back shortly or contact Matt.</p>
          </div>
        )}
        <Link to="/" data-testid="gift-home-link" className="mt-10 inline-block border border-hearth-line hover:border-hearth-ochre text-hearth-muted hover:text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
          Back to the Fire
        </Link>
      </main>
      <Footer />
    </div>
  );
}
